import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MenuItem } from '@mui/material';
import { Field, FormikProvider, useFormik } from 'formik';
import { TextFieldClearable } from 'shared/ui/form-fields/text-field-clearable';
import { formValuesFromUrlSearchParams, mergeFormValuesVsCurrentSearchParams } from 'utils/url-search-params.helper';
import { PAGE_FIELD_NAME } from 'shared/hooks/paging-params';
import { TextField } from 'formik-mui';
import { UserPickerField } from 'features/user/user-picker';
import { FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';
import { useManagersDropdownList } from 'features/user/user-picker/ui';

type EmailParserFilter = {
  email: string;
  type: number | string;
  status: string;
  active: number | string;
  userId: number | null;
}

export const defaultFormValues: EmailParserFilter = {
  email: '',
  type: '',
  status: '',
  active: '',
  userId: null
};

const DEBOUNCE_DELAY = 500;

export const EmailAccountParserFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { items: managers } = useManagersDropdownList();

  const typeOptions = [
    { id: '0', label: 'Личный' },
    { id: '1', label: 'Общий' },
  ];

  const statusOptions = [
    { id: 'ERROR', label: 'ERROR' },
    { id: 'SUCCESS', label: 'SUCCESS' },
  ];

  const activeOptions = [
    { id: '0', label: 'Деактивирован' },
    { id: '1', label: 'Активирован' },
  ];

  const createOptionFormatter = (options: { id: number | string; label: string }[]) => {
    const map = new Map(options.map((item) => [String(item.id), item.label]));
    return ({ value }: ValueFormatterContext) => map.get(String(value)) ?? value;
  };

  const userFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((item) => String(item.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const typeFormatter = createOptionFormatter(typeOptions);
  const statusFormatter = createOptionFormatter(statusOptions);
  const activeFormatter = createOptionFormatter(activeOptions);

  const isValueEqual = (left: any, right: any) => {
    if (left == null && right == null) return true;
    if (left == null || right == null) return false;
    if (left instanceof Date && right instanceof Date) return left.getTime() === right.getTime();
    if (Array.isArray(left) || Array.isArray(right)) {
      if (!Array.isArray(left) || !Array.isArray(right)) return false;
      if (left.length !== right.length) return false;
      return left.every((value, index) => String(value) === String(right[index]));
    }
    return String(left) === String(right);
  };

  const formik = useFormik({
    initialValues: formValuesFromUrlSearchParams(searchParams, defaultFormValues),
    onSubmit: () => {},
    validateOnChange: true,
    initialTouched: { email: true },
    validate: (values)=> {
      if (!!values.email && values.email.length > 0 && values.email.length <3) {
        return { email: 'Наберите хотя бы 3 символа для поиска' };
      }
    }
  });

  useEffect(() => {
    const setCurrentSearchParams = () => {
      setSearchParams(prev => {
        const params = mergeFormValuesVsCurrentSearchParams(prev, formik.values, defaultFormValues);
        if (formik.dirty) {
          // раз пользователь обновил фильтр, сбрасываем страницу на 0
          params.set(PAGE_FIELD_NAME, '0');
        }
        return params;
      }, { replace: true });
    };

    const cancel = setTimeout(()=>{
      if (formik.isValid) {
        setCurrentSearchParams();
      }
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(cancel);
  }, [formik.values, formik.isValid]);

  useEffect(() => {
    const nextValues = formValuesFromUrlSearchParams(searchParams, defaultFormValues);
    const shouldSync = Object.keys(defaultFormValues).some((key) => !isValueEqual(formik.values[key as keyof typeof nextValues], nextValues[key as keyof typeof nextValues]));
    if (shouldSync) {
      formik.setValues(nextValues, false);
    }
  }, [searchParams]);

  return (
    <FormikProvider value={formik}>
      <FilterWrapper title="Фильтры почтовых ящиков">
        <FilterWrapper.Item label="Email" queryKeys="email">
          <Field
            fullWidth
            type="text"
            name="email"
            label="email"
            helperText="Начните набирать для фильтрации"
            component={TextFieldClearable}
          />
        </FilterWrapper.Item>

        <FilterWrapper.Item label="Пользователь" queryKeys="userId" valueFormatter={userFormatter}>
          <Field
            fullWidth
            clearable
            name="userId"
            label="Пользователь"
            component={UserPickerField}
          />
        </FilterWrapper.Item>

        <FilterWrapper.Item label="Тип" queryKeys="type" valueFormatter={typeFormatter}>
          <Field
            label='Тип'
            fullWidth
            component={TextField}
            select
            name='type'
          >
            <MenuItem
              key=""
              value=""
            >
            &nbsp;
            </MenuItem>
            <MenuItem key={0} value={"0"}>
                Личный
              </MenuItem>
            <MenuItem key={1} value={"1"}>
                Общий
            </MenuItem>
          </Field>
        </FilterWrapper.Item>

        <FilterWrapper.Item label="Статус" queryKeys="status" valueFormatter={statusFormatter}>
          <Field
            label='Статус'
            fullWidth
            component={TextField}
            select
            name='status'
          >
            <MenuItem
              key=""
              value=""
            >
            &nbsp;
            </MenuItem>
            <MenuItem key={0} value={"ERROR"}>
                ERROR
              </MenuItem>
            <MenuItem key={1} value={"SUCCESS"}>
                SUCCESS
            </MenuItem>
          </Field>
        </FilterWrapper.Item>

        <FilterWrapper.Item label="Активность" queryKeys="active" valueFormatter={activeFormatter}>
          <Field
            label='Активность'
            fullWidth
            component={TextField}
            select
            name='active'
          >
            <MenuItem
              key=""
              value=""
            >
            &nbsp;
            </MenuItem>
            <MenuItem key={0} value={"0"}>
                Деактивирован
              </MenuItem>
            <MenuItem key={1} value={"1"}>
                Активирован
            </MenuItem>
          </Field>
        </FilterWrapper.Item>
      </FilterWrapper>
    </FormikProvider>
  );
};
